import { modalContentByWorkshop } from "./modals/modal-content";
import { resolveField } from "./overrides/workshop-override-content";
import type { WorkshopOverride } from "./overrides/workshop-override-content";

/**
 * Single source for a workshop's *session date* on every display surface.
 *
 * UAT round 1: the WS4 session date was frozen in seed content as
 * "14 Mar 2026" (report meta, transcript meta, playback subtitle/timeline,
 * captures footer) while the agenda/map said "Thursday 22 May 2026" — five
 * hardcoded copies, two disagreeing values. The agenda `date` is the one
 * copy that is authored per workshop AND power-user editable (Manage
 * Workshop → Agenda), so it is the canonical source; everything else
 * derives from it at render time via {@link withSessionDate}.
 *
 * Relative times (mm:ss playback offsets, "2 weeks" due targets, agenda
 * "09:00" slots) are NOT dates and are left alone.
 */

/** Matches an absolute date token inside an authored string — "14 Mar 2026",
 *  "14 MAR 2026", "14 March 2026". Weekday prefixes are handled separately. */
const DATE_TOKEN =
  /\b\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}\b/gi;

const WEEKDAY_PREFIX = /^(?:Mon|Tues|Wednes|Thurs|Fri|Satur|Sun)day,?\s+/i;

/**
 * The workshop's effective session date — the (override-aware) agenda date
 * with any weekday prefix stripped ("Thursday 22 May 2026" → "22 May 2026").
 * Null when the workshop has no authored date yet ("Date to be confirmed"),
 * in which case callers keep their seed string untouched.
 */
export function resolveSessionDate(
  workshopId: string,
  override: WorkshopOverride | undefined,
): string | null {
  const content =
    modalContentByWorkshop[workshopId as keyof typeof modalContentByWorkshop];
  if (!content) return null;
  const raw = resolveField(override, "agenda", content.agenda).date?.trim();
  if (!raw || /to be confirmed|tbc/i.test(raw)) return null;
  return raw.replace(WEEKDAY_PREFIX, "");
}

/**
 * Replace any frozen absolute-date token in an authored string with the
 * live session date, preserving the token's casing convention (an
 * all-caps token like "14 MAR 2026" stays all-caps). No session date →
 * the string passes through unchanged.
 */
export function withSessionDate(
  text: string,
  sessionDate: string | null,
): string {
  if (!sessionDate) return text;
  return text.replace(DATE_TOKEN, (token) =>
    token === token.toUpperCase() ? sessionDate.toUpperCase() : sessionDate,
  );
}
